import type { DashboardCard } from '../../api/types';

/**
 * Which cards exist, what they are called, which band of the screen they sit
 * in, and how the agent's arrangement is reconciled with them.
 *
 * The reconciliation is the part worth being careful about. A saved layout is
 * data from an older release, so it will name cards that no longer exist and
 * omit cards added since. Dropping the unknown ones and APPENDING the new ones
 * is what stops a deploy either crashing on a stale id or silently hiding a
 * feature nobody knew shipped.
 *
 * Cards that MERGED need more than dropping, though, which is what `MIGRATIONS`
 * is for — see the comment on it.
 */

/**
 * The five bands the screen reads in, top to bottom.
 *
 * Fifteen equally-weighted cards gave the eye nowhere to start; a heading over
 * each band is what turns the grid into an argument — how many came, did they
 * book, where from, who were they, and when. The order here IS the order on
 * screen, and a band with nothing visible in it draws no heading.
 */
export type SectionId = 'traffic' | 'bookings' | 'acquisition' | 'audience' | 'behaviour';

export const SECTIONS: { id: SectionId; label: string }[] = [
  { id: 'traffic', label: 'Traffic' },
  { id: 'bookings', label: 'Reservations' },
  { id: 'acquisition', label: 'Acquisition' },
  { id: 'audience', label: 'Audience' },
  { id: 'behaviour', label: 'Behaviour' },
];

export interface CardSpec {
  id: string;
  title: string;
  /** Default width in grid columns. */
  w: 1 | 2 | 3;
  section: SectionId;
}

/**
 * The headline figures and the crawler count are deliberately NOT here.
 *
 * They became page furniture — the six numbers sit on the ground above the
 * grid and the excluded count is the footnote under them — because they are
 * what every other card is read against. A card you can drag to the bottom or
 * hide is the wrong shape for that, and `excluded` was a whole card carrying
 * one number.
 */
export const CARD_CATALOGUE: CardSpec[] = [
  { id: 'trend', title: 'Visitors over time', w: 2, section: 'traffic' },
  { id: 'live', title: 'On the site now', w: 1, section: 'traffic' },
  { id: 'bookings', title: 'From visit to reservation', w: 3, section: 'bookings' },
  { id: 'acquisition', title: 'Where they came from', w: 2, section: 'acquisition' },
  { id: 'content', title: 'What they looked at', w: 1, section: 'acquisition' },
  { id: 'geo', title: 'Where they are', w: 2, section: 'audience' },
  { id: 'devices', title: 'Devices', w: 1, section: 'audience' },
  { id: 'hours', title: 'When they visit', w: 3, section: 'behaviour' },
  { id: 'sessions', title: 'Recent visits', w: 3, section: 'behaviour' },
];

/**
 * Retired card ids, and what now answers for them.
 *
 * Seven cards drew the same ranked list of a different dimension, so they fold
 * into three that carry the dimensions as tabs; the funnel and the conversion
 * figures were two halves of one question and are now one card. Without this
 * map `reconcile` would simply drop the old ids, and every agent who had ever
 * arranged their dashboard would lose those cards silently on deploy — they
 * would come back APPENDED at the end under their new names, below whatever
 * they had pushed to the bottom.
 *
 * `headline` and `excluded` map to nothing on purpose: they left the grid
 * rather than merging, so dropping them is the correct outcome.
 */
export const MIGRATIONS: Record<string, string> = {
  channels: 'acquisition',
  sources: 'acquisition',
  campaigns: 'acquisition',
  pages: 'content',
  entry: 'content',
  funnel: 'bookings',
  conversions: 'bookings',
  traffic: 'hours',
};

export const DEFAULT_LAYOUT: DashboardCard[] = CARD_CATALOGUE.map((card) => ({ id: card.id, w: card.w }));

export const specOf = (id: string): CardSpec | undefined => CARD_CATALOGUE.find((card) => card.id === id);

export const clampWidth = (width: number): 1 | 2 | 3 => {
  const rounded = Math.round(Number(width) || 1);
  if (rounded <= 1) return 1;
  if (rounded >= 3) return 3;
  return 2;
};

/**
 * The saved arrangement, made safe to render.
 *
 * Unknown ids are dropped, retired ones are rewritten through `MIGRATIONS`,
 * duplicates collapse, and anything the catalogue has gained since the layout
 * was saved is appended at the end. An empty or absent layout falls back to the
 * default rather than an empty screen.
 *
 * Two rules govern what a merge inherits, and both fail toward SHOWING things:
 * a successor is hidden only when every card that fed into it was hidden (one
 * hidden campaigns card must not take referrers away too), and it takes the
 * catalogue's width rather than a predecessor's, because a width chosen for a
 * third of the grid says nothing about a card that is now two thirds of it.
 */
export const reconcile = (saved: DashboardCard[] | null | undefined): DashboardCard[] => {
  if (!Array.isArray(saved) || !saved.length) return [...DEFAULT_LAYOUT];
  const kept = new Map<string, DashboardCard>();
  for (const card of saved) {
    const savedId = card?.id;
    if (!savedId) continue;
    const id = MIGRATIONS[savedId] ?? savedId;
    const spec = specOf(id);
    if (!spec) continue;
    const merged = id !== savedId;
    const existing = kept.get(id);
    if (existing) {
      if (existing.hidden && card.hidden !== true) existing.hidden = false;
      continue;
    }
    kept.set(id, {
      id,
      w: merged ? spec.w : clampWidth(card.w ?? spec.w),
      hidden: card.hidden === true,
    });
  }
  for (const spec of CARD_CATALOGUE) {
    if (!kept.has(spec.id)) kept.set(spec.id, { id: spec.id, w: spec.w });
  }
  return [...kept.values()];
};

/** Move a card to a new index, keeping everything else in order. */
export const reorder = (cards: DashboardCard[], from: number, to: number): DashboardCard[] => {
  if (from === to || from < 0 || from >= cards.length) return cards;
  const next = [...cards];
  const [moved] = next.splice(from, 1);
  next.splice(Math.max(0, Math.min(next.length, to)), 0, moved);
  return next;
};

/**
 * Drop `fromId` onto `toId`'s slot.
 *
 * Refuses across sections, which is the one freedom the bands cost: a card can
 * be reordered inside the band it belongs to and not moved out of it. The
 * heading is a claim about what is underneath it, and a Devices card under
 * Traffic would make that claim false — where before, with no headings, any
 * card anywhere was merely a preference.
 */
export const moveCard = (cards: DashboardCard[], fromId: string, toId: string): DashboardCard[] => {
  if (fromId === toId) return cards;
  const from = specOf(fromId);
  const to = specOf(toId);
  if (!from || !to || from.section !== to.section) return cards;
  const fromIndex = cards.findIndex((card) => card.id === fromId);
  const toIndex = cards.findIndex((card) => card.id === toId);
  if (fromIndex < 0 || toIndex < 0) return cards;
  return reorder(cards, fromIndex, toIndex);
};

/** 1 -> 2 -> 3 -> 1. A three-way toggle beats a dropdown for one number. */
export const cycleWidth = (cards: DashboardCard[], id: string): DashboardCard[] =>
  cards.map((card) => (card.id === id ? { ...card, w: clampWidth(card.w === 3 ? 1 : card.w + 1) } : card));

export const toggleHidden = (cards: DashboardCard[], id: string): DashboardCard[] =>
  cards.map((card) => (card.id === id ? { ...card, hidden: !card.hidden } : card));

/** What actually renders, in order. */
export const visibleCards = (cards: DashboardCard[]): DashboardCard[] =>
  cards.filter((card) => !card.hidden && Boolean(specOf(card.id)));

export interface CardGroup {
  id: SectionId;
  label: string;
  cards: DashboardCard[];
}

/**
 * The visible cards, banded and in section order.
 *
 * A band with nothing left in it is dropped rather than drawn empty — an agent
 * who hides both audience cards should lose the heading too, not keep a rule
 * with nothing under it.
 */
export const groupCards = (cards: DashboardCard[]): CardGroup[] => {
  const shown = visibleCards(cards);
  return SECTIONS.map((section) => ({
    id: section.id,
    label: section.label,
    cards: shown.filter((card) => specOf(card.id)?.section === section.id),
  })).filter((group) => group.cards.length > 0);
};
